import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import dataService from "../../../helpers/dataService";
import tools from "../../../helpers/tools";
import { Pill } from "../../common/DataList";
import { STATUT_TONES } from "../SuspensionRemobilisation/suspensionRemobilisationLabels";
import "../SuspensionRemobilisation/suspension-remobilisation.scss";

// Décisions métier du 15/09/2026 : l'historique EP du dossier décide seul de
// l'issue, le référent unique ne choisit que le motif. L'historique est montré
// à titre informatif : la dernière suspension appliquée est rattachée d'office,
// une inscription à venir est annulée dès l'enregistrement.
const ISSUES = {
  REMOBILISATION: {
    titre: "Remobilisation",
    texte: (contexte) => {
      const inscriptions = contexte.pendingEpRegistrations || [];
      return (
        <>
          La demande sera rattachée à la <strong>dernière suspension appliquée</strong> et soumise au
          <strong> Directeur de la Caf</strong> ou à son délégataire, prévenu par courriel. Après validation,
          l'allocataire sera notifié par courrier et la liquidation recevra un bon de travail.
          {inscriptions.length > 0 && (
            <>
              {" "}L'inscription à l'EP du <strong>{tools.formatDate(inscriptions[0].epDate)}</strong> sera annulée
              dès l'enregistrement, <strong>sans attendre</strong> la décision : un refus ne la rétablira pas.
            </>
          )}
        </>
      );
    },
  },
  LEVEE_DE_SAISINE: {
    titre: "Levée de saisine",
    texte: (contexte) => {
      const inscription = (contexte.pendingEpRegistrations || [])[0];
      return (
        <>
          Aucune sanction passée sur ce dossier : la demande est enregistrée <strong>immédiatement</strong>, sans
          décision à attendre.
          {inscription && (
            <> L'inscription à l'EP du <strong>{tools.formatDate(inscription.epDate)}</strong> est retirée de l'ordre du jour aussitôt.</>
          )}
        </>
      );
    },
  },
};

const Retrait = ({ retrait }) => {
  if (!retrait) return null;
  return retrait.done ? (
    <p className="suspension-remobilisation__mention suspension-remobilisation__mention--ok mb-2">
      <i className="fa fa-calendar-times" aria-hidden="true" />
      Inscription EP du {tools.formatDate(retrait.epDate)} — annulée
    </p>
  ) : (
    <p className="suspension-remobilisation__mention suspension-remobilisation__mention--warn mb-2">
      <i className="fa fa-exclamation-triangle" aria-hidden="true" />
      Inscription EP du {tools.formatDate(retrait.epDate)} — <strong>non annulée</strong> : {retrait.reasonNotDone}
    </p>
  );
};

// En-tête de la modale : titre, allocataire, pastille éventuelle. La modale
// générique n'a pas d'en-tête propre ; celui-ci suit le kit Fiche (sur-titre
// discret, titre h2 puisque la page garde son h1).
const Entete = ({ titre, nom, pill = null }) => (
  <header className="suspension-remobilisation__entete">
    <h2 className="suspension-remobilisation__titre">{titre}</h2>
    <p className="suspension-remobilisation__allocataire">{nom}</p>
    {pill}
  </header>
);

const DemanderSuspensionRemobilisationModal = ({ recipient, refresh = () => {} }) => {
  const [contexte, setContexte] = useState(null);
  const [erreurContexte, setErreurContexte] = useState(null);
  const [motif, setMotif] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [resultat, setResultat] = useState(null);

  useEffect(() => {
    if (!recipient?.id) return;
    dataService.get(
      `sanctions/suspension-remobilisation/recipients/${recipient.id}/contexte`,
      (data) => setContexte(data),
      (err) => setErreurContexte(err?.message || "Le dossier n'a pas pu être lu")
    );
  }, [recipient]);

  const nom = `${recipient.lastname || ""} ${recipient.firstname || ""}`.trim();

  const enregistrer = () => {
    setIsSaving(true);
    dataService.post(
      "sanctions/suspension-remobilisation",
      { recipientId: recipient.id, reason: motif },
      (data) => {
        setResultat(data);
        // La fiche se rafraîchit dès l'enregistrement : la fermeture, elle,
        // est portée par le pied générique des modales.
        refresh();
      },
      (err) => toast.error(err?.message || "La demande n'a pas pu être enregistrée"),
      () => setIsSaving(false)
    );
  };

  // ----- Écran de résultat -----
  if (resultat) {
    const sanction = resultat.targetedSanction;
    const remobilisation = resultat.outcome === "REMOBILISATION";
    return (
      <div className="suspension-remobilisation">
        <Entete
          titre={resultat.outcomeLabel}
          nom={nom}
          pill={<Pill tone={STATUT_TONES[resultat.status] ?? "neutral"}>{resultat.statusLabel}</Pill>}
        />

        {sanction && (
          <p className="mb-2">
            <strong>Suspension rattachée :</strong> {sanction.label}
            {sanction.epDate ? `, décidée à l'EP du ${tools.formatDate(sanction.epDate)}` : ""}.
          </p>
        )}
        <Retrait retrait={resultat.epRegistrationWithdrawal} />

        {remobilisation ? (
          <div className="suspension-remobilisation__attente" role="status">
            La demande est enregistrée et attend la décision du <strong>Directeur de la Caf</strong> ou de son délégataire,
            prévenu par courriel.
          </div>
        ) : (
          <div className="suspension-remobilisation__succes" role="status">
            <i className="fa fa-check-circle mr-1" aria-hidden="true" />
            La levée de saisine est enregistrée dans le parcours de l'allocataire. Rien d'autre à attendre.
          </div>
        )}
      </div>
    );
  }

  if (erreurContexte) {
    return (
      <div className="suspension-remobilisation">
        <Entete titre="Suspension / Remobilisation" nom={nom} />
        <div className="alert alert-danger mb-0" role="alert">{erreurContexte}</div>
      </div>
    );
  }

  if (!contexte) {
    return (
      <div className="suspension-remobilisation">
        <Entete titre="Suspension / Remobilisation" nom={nom} />
        <p className="suspension-remobilisation__lecture" role="status">
          <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true" />
          Lecture du dossier…
        </p>
      </div>
    );
  }

  const suspensions = contexte.establishedSuspensions || [];
  const inscriptions = contexte.pendingEpRegistrations || [];
  const historique = suspensions.length + inscriptions.length > 0 && (
    <div className="form-group">
      <label className="font-weight-bold">Historique EP du dossier</label>
      <ul className="list-unstyled mb-0 suspension-remobilisation__historique">
        {suspensions.map((s) => {
          const rattachee = s.epId === contexte.defaultSanctionEpId;
          return (
            <li
              key={`sanction-${s.epId}`}
              className={`suspension-remobilisation__suspension${rattachee ? "" : " is-terminee"}`}
            >
              <strong>{s.label}</strong>
              <Pill tone={s.ongoing ? "warn" : "muted"} className="suspension-remobilisation__badge">
                {s.ongoing ? `En cours jusqu'au ${tools.formatDate(s.endOfPeriod)}` : `Terminée le ${tools.formatDate(s.endOfPeriod)}`}
              </Pill>
              <span className="suspension-remobilisation__detail">
                Décidée à l'EP du {tools.formatDate(s.epDate)}, notifiée le {tools.formatDate(s.notifiedAt)}
              </span>
              {rattachee && (
                <span className="suspension-remobilisation__mention suspension-remobilisation__mention--ok">
                  <i className="fa fa-check-circle" aria-hidden="true" />
                  Rattachée à cette demande
                </span>
              )}
            </li>
          );
        })}
        {inscriptions.map((i) => (
          <li key={`inscription-${i.epRegistrationId}`} className="suspension-remobilisation__inscription">
            <strong>Inscription à l'EP du {tools.formatDate(i.epDate)}</strong>
            <Pill tone="info" className="suspension-remobilisation__badge">Commission non finalisée</Pill>
            <span className="suspension-remobilisation__detail">{i.label}</span>
            <span className="suspension-remobilisation__mention suspension-remobilisation__mention--ko">
              <i className="fa fa-calendar-times" aria-hidden="true" />
              Sera annulée dès l'enregistrement
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  if (contexte.blocker) {
    return (
      <div className="suspension-remobilisation">
        <Entete titre="Suspension / Remobilisation" nom={nom} />
        {historique}
        <div className="suspension-remobilisation__blocage" role="status">
          <i className="fa fa-info-circle mr-1" aria-hidden="true" />
          {contexte.blockerLabel}
          {contexte.activeRequest ? ` (${contexte.activeRequest.statusLabel}).` : ""}
        </div>
      </div>
    );
  }

  const issue = ISSUES[contexte.expectedAction];
  const peutEnregistrer = !!motif && !!issue && !isSaving;

  return (
    <div className="suspension-remobilisation">
      <Entete titre="Suspension / Remobilisation" nom={nom} />
      {historique}

      {issue && (
        <div className="suspension-remobilisation__issue">
          <strong>{issue.titre}.</strong> {issue.texte(contexte)}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="suspension-remobilisation-motif" className="font-weight-bold">Motif</label>
        <select
          id="suspension-remobilisation-motif"
          className="form-control suspension-remobilisation__motif"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        >
          <option value="">— Choisir un motif —</option>
          {(contexte.reasons || []).map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
      </div>

      <div className="suspension-remobilisation__actions">
        <button
          type="button"
          className="btn btn-primary suspension-remobilisation__enregistrer"
          onClick={enregistrer}
          disabled={!peutEnregistrer}
        >
          {isSaving ? (
            <>
              <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true" />
              Enregistrement…
            </>
          ) : (
            "Enregistrer la demande"
          )}
        </button>
      </div>
    </div>
  );
};

export default DemanderSuspensionRemobilisationModal;
